import type * as z from "zod";
import type { pushLive as pushLiveTool } from "../../api/ai/tools/pushLive";
import type { StepFailure } from "../lib/stepTestFailures";
import { useState } from "react";
import { ChatActionPrompt } from "../components/Chat/ChatActionPrompt";
import type {
  ChatTool,
  PendingPromptArgs,
  ToolUseProps,
} from "../components/Chat/ChatTool";
import { ToolStatus } from "../components/Chat/ToolStatus";
import { PushLivePreflight } from "../components/PushLivePreflight";
import { Text } from "../components/Text";
import { useMainMergeStatus } from "../hooks/useMainMergeStatus";
import { usePushLive } from "../hooks/usePushLive";
import type { PushLiveBranch } from "../lib/executePushLive";
import { uploadToolCallOutput } from "../lib/toolCallOutputs";
import { emitErrorToast } from "../lib/errorToast";

type Input = z.infer<ReturnType<typeof pushLiveTool>["inputSchema"]>;
type UnparsedInput = { [K in keyof Input]?: unknown };
type Output = { pushed?: unknown };

export function parseSummary(input: unknown): string | null {
  if (!input || typeof input !== "object" || Array.isArray(input)) return null;
  const { summary } = input as UnparsedInput;
  return typeof summary === "string" && summary.trim() ? summary : null;
}

function parseOutput(output: unknown): { pushed: boolean } | null {
  if (!output || typeof output !== "object" || Array.isArray(output))
    return null;
  const pushed = (output as Output).pushed;
  return typeof pushed === "boolean" ? { pushed } : null;
}

function PushLiveHeader({ result, interrupted }: ToolUseProps) {
  if (!result || result.isError) {
    return (
      <ToolStatus icon="rocket" text="Push live?" pending={!interrupted} />
    );
  }
  const output = parseOutput(result.output);
  if (!output?.pushed) {
    return <ToolStatus icon="circle-minus" text="Push live cancelled" />;
  }
  return (
    <ToolStatus icon="rocket" iconColor="var(--green-600)" text="Pushed live" />
  );
}

function PushLiveBody({ toolUse }: ToolUseProps) {
  const summary = parseSummary(toolUse.input);
  if (!summary) return null;
  return (
    <div style={{ padding: "2px 6px 8px 6px" }}>
      <Text variant="text-smaller-md" color="var(--text-tertiary)">
        {summary}
      </Text>
    </div>
  );
}

type OnResult = (
  output: unknown,
  isError: boolean,
  options?: { pauseAfter?: boolean }
) => Promise<boolean>;

export function PushLivePrompt({
  branch,
  mainBranchId,
  spaceId,
  canPushLive,
  canWrite,
  blockedBySelfMerge,
  gitSynced,
  onResult,
  onInvestigateFailures,
  stayAfterPush,
  toolCall,
  workflowName,
  summary,
}: {
  branch: PushLiveBranch | undefined;
  mainBranchId: string | undefined;
  spaceId: string;
  canPushLive: boolean;
  canWrite: boolean;
  blockedBySelfMerge: boolean;
  gitSynced: boolean;
  onResult: OnResult;
  onInvestigateFailures?: (failures: readonly StepFailure[]) => void;
  stayAfterPush?: boolean;
  toolCall?: { chatId: string; entryId: string };
  workflowName?: string;
  summary?: string | null;
}) {
  const { pushLive, mergeMain } = usePushLive({
    branchId: branch?.id ?? "",
    spaceId,
    stayAfterPush,
  });
  const mergeStatus = useMainMergeStatus(branch?.id ?? "", mainBranchId);
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [pushing, setPushing] = useState(false);

  const cancel = () => onResult({ pushed: false }, false, { pauseAfter: true });

  const confirm = async (copyVolumesToLive: boolean) => {
    if (!toolCall) {
      const saved = await onResult({ pushed: true }, false, {
        pauseAfter: true,
      });
      if (saved) void pushLive(branch, mainBranchId, copyVolumesToLive);
      return;
    }
    setPushing(true);
    try {
      const outputId = await uploadToolCallOutput({
        ...toolCall,
        output: { pushed: true },
      });
      await pushLive(branch, mainBranchId, copyVolumesToLive, {
        ...toolCall,
        outputId,
      });
    } catch {
      emitErrorToast({
        icon: "circle-off",
        message: "The workflow couldn’t be pushed live. Try again.",
        buttonLabel: "Dismiss",
      });
    }
    setPushing(false);
  };

  const resolveConflicts = () => {
    onResult({ pushed: false }, false, { pauseAfter: true });
    mergeMain();
  };

  const blockedReason = gitSynced
    ? "This space syncs with a git repository. Sync the draft and merge it on the remote instead."
    : !canPushLive
      ? "You don’t have permission to push live in this space."
      : blockedBySelfMerge
        ? "This space requires a different user to push branches live."
        : !branch || branch.name === "main"
          ? "There’s nothing to push — you’re on the live branch."
          : null;

  const conflictReason =
    mergeStatus === "unresolved"
      ? "This draft has unresolved conflicts. Resolve them before pushing live."
      : mergeStatus === "conflicts"
        ? canWrite
          ? "Live has changes that conflict with this draft. Pull them in and resolve the conflicts before pushing live."
          : "Live has changes that conflict with this draft. Someone with edit access needs to pull them in and resolve the conflicts before it can be pushed live."
        : undefined;
  const canResolveConflicts =
    !blockedReason && mergeStatus === "conflicts" && canWrite;

  return (
    <>
      <ChatActionPrompt
        title={
          workflowName
            ? `Push “${workflowName}” live?`
            : "Push this workflow live?"
        }
        notification="Push live?"
        description={blockedReason ?? conflictReason ?? summary ?? undefined}
        confirmLabel={
          canResolveConflicts ? "Pull in live changes" : "Push live"
        }
        cancelLabel="Keep building"
        disabled={preflightOpen || pushing}
        confirmDisabled={
          !!blockedReason || (mergeStatus !== "ready" && !canResolveConflicts)
        }
        onConfirm={
          canResolveConflicts ? resolveConflicts : () => setPreflightOpen(true)
        }
        onCancel={cancel}
      />
      {preflightOpen && branch && (
        <PushLivePreflight
          workflowId={branch.workflowId}
          branchId={branch.id}
          spaceId={spaceId}
          onProceed={(copyVolumesToLive) => {
            setPreflightOpen(false);
            void confirm(copyVolumesToLive);
          }}
          onCancel={() => setPreflightOpen(false)}
          onInvestigateFailures={
            onInvestigateFailures &&
            (async (params) => {
              const saved = await onResult({ pushed: false }, false, {
                pauseAfter: true,
              });
              if (saved) onInvestigateFailures(params);
            })
          }
        />
      )}
    </>
  );
}

export const pushLiveActivity: ChatTool = {
  name: "pushLive",
  Header: PushLiveHeader,
  Body: PushLiveBody,
  alwaysLoadOutput: true,
};

type PushLiveOptions = {
  branch: PushLiveBranch | undefined;
  mainBranchId: string | undefined;
  spaceId: string;
  canPushLive: boolean;
  canWrite: boolean;
  blockedBySelfMerge: boolean;
  gitSynced: boolean;
  onInvestigateFailures: (failures: readonly StepFailure[]) => void;
};

function PushLivePendingPrompt({ onResult, input, data }: PendingPromptArgs) {
  const opts = data as PushLiveOptions;
  return (
    <PushLivePrompt
      branch={opts.branch}
      mainBranchId={opts.mainBranchId}
      spaceId={opts.spaceId}
      canPushLive={opts.canPushLive}
      canWrite={opts.canWrite}
      blockedBySelfMerge={opts.blockedBySelfMerge}
      gitSynced={opts.gitSynced}
      onResult={onResult}
      onInvestigateFailures={opts.onInvestigateFailures}
      summary={parseSummary(input)}
    />
  );
}

export const pushLive = (opts: PushLiveOptions): ChatTool => ({
  ...pushLiveActivity,
  data: opts,
  PendingPrompt: PushLivePendingPrompt,
});
